import { useMemo, useState } from "react";
import { todayKey } from "@/domain/dates";
import type { Entry, Scenario, Subscription } from "@/domain/entities";
import type { LocalizedText } from "@/domain/localized";
import { lastDays, thisMonth, thisWeek, thisYear, type Period } from "@/domain/reports/periods";
import type { QuestionReport, Report } from "@/domain/reports/ReportBuilder";
import { TIME_PERIOD_OPTIONS } from "@/domain/scenario/timePeriods";
import { ScenarioSwitch } from "../components/ScenarioSwitch";
import { Button } from "../components/Button";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Screen";
import { useApp } from "../container";
import { useActiveSubscription } from "../hooks/useActiveSubscription";
import { useQuery } from "../hooks/useQuery";
import { useLocale } from "../i18n/LocaleProvider";
import { buildCsv, loadPdfFont, shareFile } from "../export/ReportExport";
import { format, type Strings } from "../i18n/strings";
import styles from "./ReportsScreen.module.css";

type PeriodKey = "week" | "month" | "year" | "custom";
const PERIOD_KEYS: PeriodKey[] = ["week", "month", "year", "custom"];

interface Block {
  subscription: Subscription;
  scenario: Scenario;
  entries: Entry[];
  report: Report;
}

function periodFor(key: PeriodKey, today: string, custom: Period): Period {
  switch (key) {
    case "week": return thisWeek(today);
    case "month": return thisMonth(today);
    case "year": return thisYear(today);
    case "custom": return custom.from <= custom.to ? custom : { from: custom.to, to: custom.from };
  }
}

function describeQuestion(question: QuestionReport, scenario: Scenario, t: Strings, pick: (text: LocalizedText) => string): string[] {
  switch (question.type) {
    case "yesno":
      return [format(scenario.episodes ? t.reports.yesEntries : t.reports.yesDays, { yes: question.yes, answered: question.answered })];
    case "scale":
    case "number":
      return [`${format(t.reports.average, { avg: question.avg.toFixed(1) })}, ${format(t.reports.range, { min: question.min, max: question.max })}`];
    case "single":
    case "multi": {
      const definition = scenario.questions.find((q) => q.id === question.questionId);
      const options = definition && (definition.type === "single" || definition.type === "multi") ? definition.options : [];
      return question.options
        .filter((o) => o.count > 0)
        .map((o) => {
          const option = options.find((x) => x.id === o.optionId);
          const label = option ? pick(option.text) : t.reports.other;
          return `${label} · ${o.count}`;
        });
    }
    case "medication":
      return question.byName.map((m) => `${m.name} · ${format(t.reports.intakes, { intakes: m.intakes })}, ${format(t.reports.helped, { helped: m.helped })}`);
    case "time":
      if (!question.options) return [format(t.reports.answered, { answered: question.answered })];
      return question.options
        .filter((o) => o.count > 0)
        .map((o) => {
          const option = TIME_PERIOD_OPTIONS.find((x) => x.id === o.optionId);
          return `${option ? pick(option.text) : o.optionId} · ${o.count}`;
        });
    case "text":
      return [format(t.reports.answered, { answered: question.answered })];
  }
}

export function ReportsScreen() {
  const app = useApp();
  const { t, pick, locale } = useLocale();
  const today = todayKey(app.clock);
  const [periodKey, setPeriodKey] = useState<PeriodKey>("month");
  const [choosingFormat, setChoosingFormat] = useState(false);
  const [shareState, setShareState] = useState<"idle" | "busy" | "failed">("idle");
  const [custom, setCustom] = useState<Period>(() => lastDays(today, 30));
  const period = useMemo(() => periodFor(periodKey, today, custom), [periodKey, today, custom]);
  const active = useActiveSubscription();
  const subscription = active.selected;
  const scenario = active.scenarios.find((s) => s.id === subscription?.scenarioId);

  const blocks = useQuery<Block[]>(async () => {
    if (!subscription || !scenario) return [];
    const own: Entry[] = await app.store.entries.listBySubscription(subscription.id, period);
    return [{ subscription, scenario, entries: own, report: app.reports.build(scenario, own, period) }];
  }, [period.from, period.to, today, subscription?.id ?? "", subscription?.updatedAt ?? ""], app.changeSources);

  const share = async (kind: "csv" | "pdf", block: Block) => {
    setShareState("busy");
    try {
      const summary = block.report.questions
        .filter((q) => q.answered > 0)
        .map((q) => {
          const definition = block.scenario.questions.find((x) => x.id === q.questionId);
          return { label: definition ? pick(definition.text) : q.questionId, lines: describeQuestion(q, block.scenario, t, pick) };
        });
      const input = { scenario: block.scenario, entries: block.entries, period, report: block.report, locale, t, summary };
      const base = `u-memo-${block.scenario.slug}-${period.from}-${period.to}`;
      const file = kind === "csv"
        ? new File([buildCsv(input)], `${base}.csv`, { type: "text/csv" })
        : new File([new Uint8Array(await (await import("../export/PdfExport")).buildPdf(input, await loadPdfFont(locale)))], `${base}.pdf`, { type: "application/pdf" });
      app.analytics.track("report_share", { format: kind });
      await shareFile(file, `${t.appName} · ${pick(block.scenario.name)}`);
      setShareState("idle");
      setChoosingFormat(false);
    } catch {
      setShareState("failed");
    }
  };

  return (
    <Screen title={t.reports.title}>
      <ScenarioSwitch subscriptions={active.subscriptions} scenarios={active.scenarios} value={subscription?.id} onChange={(id) => { app.analytics.track("report_scenario_switch"); active.select(id); }} />
      {!active.loading && !subscription && <p className={styles.muted}>{t.calendar.noScenarios}</p>}
      <div className={styles.periods}>
        <Segmented
          options={PERIOD_KEYS.map((key) => ({ id: key, label: t.reports[key] }))}
          value={periodKey}
          onChange={(key) => { app.analytics.track("report_period", { period: key }); setPeriodKey(key as PeriodKey); }}
          ariaLabel={t.reports.title}
        />
      </div>
      {periodKey === "custom" && (
        <div className={styles.range}>
          <label>
            {t.reports.from}
            <input className={styles.date} type="date" max={today} value={custom.from} onChange={(e) => e.target.value && setCustom({ ...custom, from: e.target.value })} />
          </label>
          <label>
            {t.reports.to}
            <input className={styles.date} type="date" max={today} value={custom.to} onChange={(e) => e.target.value && setCustom({ ...custom, to: e.target.value })} />
          </label>
        </div>
      )}
      {blocks.data?.map(({ subscription, scenario, report }) => (
        <section key={subscription.id} className={styles.block}>
          <h2 className={styles.name}>{pick(scenario.name)}</h2>
          <p className={styles.muted}>
            <span>{format(t.reports.recorded, { recorded: report.recorded, days: report.days })}</span>
            {report.skipped > 0 && <span>, {format(t.reports.skipped, { skipped: report.skipped })}</span>}
            {report.episodes !== null && <span>, {format(t.reports.episodes, { episodes: report.episodes })}</span>}
          </p>
          {report.recorded === 0 && <p className={styles.muted}>{t.reports.noData}</p>}
          {report.recorded > 0 && (
            <div className={styles.rows}>
              {report.questions
                .filter((q) => q.answered > 0)
                .map((q) => {
                  const definition = scenario.questions.find((x) => x.id === q.questionId);
                  return (
                    <div key={q.questionId} className={styles.row}>
                      <span className={styles.label}>{definition ? pick(definition.text) : q.questionId}</span>
                      {describeQuestion(q, scenario, t, pick).map((line) => <span key={line}>{line}</span>)}
                    </div>
                  );
                })}
            </div>
          )}
        </section>
      ))}
      {blocks.data?.map((block) => (
        <div key={`share-${block.subscription.id}`} className={styles.share}>
          {!choosingFormat && (
            <Button variant="primary" block onClick={() => setChoosingFormat(true)} disabled={block.report.entries === 0}>{t.reports.share}</Button>
          )}
          {choosingFormat && (
            <div className={styles.shareRow} role="group" aria-label={t.reports.share}>
              <Button onClick={() => share("csv", block)} disabled={shareState === "busy"}>{t.reports.shareCsv}</Button>
              <Button onClick={() => share("pdf", block)} disabled={shareState === "busy"}>{t.reports.sharePdf}</Button>
              <Button variant="ghost" onClick={() => { setChoosingFormat(false); setShareState("idle"); }}>{t.common.cancel}</Button>
            </div>
          )}
          {shareState === "busy" && <p className={styles.muted}>{t.reports.sharePreparing}</p>}
          {shareState === "failed" && <p className={styles.muted} role="alert">{t.reports.shareFailed}</p>}
        </div>
      ))}
    </Screen>
  );
}
